import { PortfolioExposure, PortfolioReviewBlock } from "creso-core/types";
import { formatAsPercentage } from "creso-core/utils";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { IDEAL_FUND_COUNT } from "../constants";
import {
  ReviewBlockState,
  ReviewNotice,
  ReviewSection,
} from "./review-section";

const formatPct = (value: string | number) => formatAsPercentage(value, "-", 0);

export function ExposureSection({
  block,
  isLoading,
}: {
  block?: PortfolioReviewBlock<PortfolioExposure>;
  isLoading: boolean;
}) {
  const exposure = block?.data;

  return (
    <ReviewSection title="Exposure">
      <ReviewBlockState
        isLoading={isLoading}
        isError={block?.ok === false}
        isEmpty={!!exposure && !exposure.fund_count}
        emptyMessage="No holdings to measure exposure on."
      >
        {exposure ? (
          <>
            <div className="flex flex-col gap-2 md:flex-row">
              <ReviewNotice className="flex-1">
                {exposure.fund_count} funds from {exposure.amc_count} AMCs
              </ReviewNotice>
              <FundCountRange count={exposure.fund_count} />
            </div>

            {exposure.top_amcs.length ? (
              <div className="flex flex-col gap-4">
                <h6>
                  Top {exposure.top_amcs.length} AMCs:{" "}
                  {formatPct(exposure.top_3_pct)}
                </h6>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  {exposure.top_amcs.map((amc) => (
                    <div
                      key={amc.amc_morningstar_code ?? amc.amc_name}
                      className="flex items-center gap-3 rounded-lg border border-border py-2 pl-2 pr-4"
                    >
                      <AmcLogo amc_code={amc.amc_morningstar_code} />
                      <span className="flex-1 truncate text-xs">
                        {amc.amc_name}
                      </span>
                      <span className="text-sm font-semibold">
                        {formatPct(amc.pct)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex flex-col gap-4">
              <h6>Top overlaps</h6>
              {exposure.overlap_pairs.length ? (
                exposure.overlap_pairs.slice(0, 3).map((pair) => (
                  <div
                    key={`${pair.isin_a}-${pair.isin_b}`}
                    className="relative grid grid-cols-[1fr_auto_1fr] items-center rounded-2xl border border-border text-sm"
                  >
                    <span className="truncate px-4 py-3.5">{pair.name_a}</span>
                    <span className="flex h-12 w-25 items-center justify-center rounded-2xl border border-border bg-card font-semibold shadow-xs">
                      {formatPct(pair.overlap_pct)}
                    </span>
                    <span className="truncate px-4 py-3.5 text-right">
                      {pair.name_b}
                    </span>
                  </div>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">
                  No fund pairs overlap by 25% or more.
                </span>
              )}
            </div>
          </>
        ) : null}
      </ReviewBlockState>
    </ReviewSection>
  );
}

function FundCountRange({ count }: { count: number }) {
  const { min, max } = IDEAL_FUND_COUNT;
  const range = `${min}-${max} funds`;
  if (count < min) {
    return (
      <ReviewNotice tone="warning" className="md:w-60">
        Below ideal range ({range})
      </ReviewNotice>
    );
  }
  if (count > max) {
    return (
      <ReviewNotice tone="warning" className="md:w-60">
        Above ideal range ({range})
      </ReviewNotice>
    );
  }
  return (
    <ReviewNotice tone="positive" className="md:w-60">
      Within ideal range ({range})
    </ReviewNotice>
  );
}
